import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { RiLoader4Line } from 'react-icons/ri';

interface GlassButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    icon?: ReactNode;
    gradient?: boolean;
    loading?: boolean;
}

export default function GlassButton({
    children,
    variant = 'primary',
    size = 'md',
    icon,
    className = '',
    gradient = false,
    loading = false,
    ...props
}: GlassButtonProps) {

    const baseStyles = "relative overflow-hidden transition-all duration-300 font-medium rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";

    // Strict V2 Theme Variants (Monochrome)
    const variants = {
        primary: "bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 shadow-lg shadow-black/10 dark:shadow-white/10 border border-black/10 dark:border-white/50",
        secondary: "bg-black/[0.05] hover:bg-black/[0.1] text-gray-900 border border-black/[0.1] dark:bg-white/[0.05] dark:hover:bg-white/[0.1] dark:text-white dark:border-white/[0.1] backdrop-blur-md",
        danger: "bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-500 border border-red-500/20",
        outline: "bg-transparent border border-white/20 text-white hover:border-white/50 hover:bg-white/5",
        ghost: "bg-transparent text-gray-400 hover:text-white hover:bg-white/5 border-transparent"
    };

    const gradientStyles = "bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:opacity-90 border-0 shadow-lg shadow-blue-500/25";

    const sizes = {
        sm: "px-3 py-1.5 text-sm",
        md: "px-5 py-2.5 text-base",
        lg: "px-6 py-3 text-lg"
    };

    return (
        <button
            className={`${baseStyles} ${gradient ? gradientStyles : variants[variant]} ${sizes[size]} ${className}`}
            disabled={props.disabled || loading}
            {...props}
        >
            {loading ? (
                <RiLoader4Line className="animate-spin text-xl" />
            ) : (
                <>
                    {icon && <span className="text-lg">{icon}</span>}
                    {children}
                </>
            )}
        </button>
    );
}
